import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FolderOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProjectCard from "@/components/ProjectCard";
import ProjectCreationModal from "@/components/ProjectCreationModal";
import LoadingScreen from "@/components/LoadingScreen";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PROJECT_STATUSES, statusLabel } from "@/lib/projectStatus";
import ListSearch from "@/components/ListSearch";
import { matchesQuery, buildHaystack } from "@/lib/search";

export default function AdminProjects() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/projects", { params: { role: "administrador" } })
      .then((r) => setProjects(r.data))
      .catch(() => toast.error("No se han podido cargar los proyectos."));
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    PROJECT_STATUSES.forEach((s) => { map[s.value] = []; });
    (projects || []).forEach((p) => {
      const key = map[p.status] ? p.status : "en_gestion";
      map[key].push(p);
    });
    return map;
  }, [projects]);

  const groupedFiltered = useMemo(() => {
    const out = {};
    for (const s of PROJECT_STATUSES) {
      out[s.value] = (grouped[s.value] || []).filter((p) => matchesQuery(search, buildHaystack(p, statusLabel(p.status))));
    }
    return out;
  }, [grouped, search]);

  if (!projects) return <LoadingScreen text="Cargando tus proyectos…" />;

  const handleCreate = async (data) => {
    try {
      const r = await api.post("/projects", data);
      setCreateOpen(false);
      toast.success("¡Proyecto creado! Aquí tienes su hoja de ruta.");
      await checkAuth();
      navigate(`/admin/proyectos/${r.data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el proyecto.");
    }
  };

  const handleStatusChange = async (project, status) => {
    if (project.status === status) return;
    const prev = projects;
    setProjects((ps) => ps.map((p) => (p.project_id === project.project_id ? { ...p, status } : p)));
    try {
      await api.put(`/projects/${project.project_id}`, { status });
      toast.success(`"${project.name}" → ${statusLabel(status)}`);
    } catch (e) {
      setProjects(prev);
      toast.error("No se pudo cambiar el estado del proyecto.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="admin-projects-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="caption mb-2">Gestión de proyectos</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Proyectos</h1>
          <p className="text-muted-foreground mt-2">Cada proyecto tiene su hoja de ruta, sus usuarios y su asistente Mori.</p>
        </div>
        {user.is_admin && (
          <Button className="rounded-full gap-2 h-11 px-5" onClick={() => setCreateOpen(true)} data-testid="new-project-button">
            <Plus className="h-4 w-4" /> Nuevo proyecto
          </Button>
        )}
      </div>

      {projects.length > 0 && (
        <div className="mb-6">
          <ListSearch value={search} onChange={setSearch} placeholder="Buscar por cualquier contenido…" testId="project-search-input" />
        </div>
      )}

      {projects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-14 text-center" data-testid="projects-empty-state">
          <div className="mx-auto h-12 w-12 rounded-xl bg-accent text-olive flex items-center justify-center mb-4">
            <FolderOpen className="h-5 w-5" />
          </div>
          <h2 className="text-xl font-semibold">Todavía no hay proyectos</h2>
          <p className="text-sm text-muted-foreground mt-2">Crea el primero para empezar a organizar la autopromoción.</p>
        </div>
      ) : (
        <Accordion
          type="multiple"
          defaultValue={PROJECT_STATUSES.map((s) => s.value)}
          className="space-y-4"
          data-testid="projects-status-groups"
        >
          {PROJECT_STATUSES.map((s) => {
            const items = groupedFiltered[s.value] || [];
            if (search.trim() && items.length === 0) return null;
            return (
              <AccordionItem
                key={s.value}
                value={s.value}
                className="rounded-2xl border border-border bg-card/40 px-5 border-b"
                data-testid={`projects-group-${s.value}`}
              >
                <AccordionTrigger className="hover:no-underline" data-testid={`projects-group-toggle-${s.value}`}>
                  <span className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                    <span className="text-base font-semibold">{s.group}</span>
                    <span className="text-xs font-mono text-muted-foreground rounded-full bg-secondary px-2 py-0.5" data-testid={`projects-group-count-${s.value}`}>
                      {items.length}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  {items.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No hay proyectos en este estado.</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                      {items.map((p, i) => (
                        <ProjectCard key={p.project_id} project={p} index={i} onStatusChange={handleStatusChange} query={search} />
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {projects.length > 0 && search.trim() && PROJECT_STATUSES.every((s) => (groupedFiltered[s.value] || []).length === 0) && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground mt-4" data-testid="projects-no-match">
          Ningún proyecto coincide con la búsqueda.
        </div>
      )}

      <ProjectCreationModal open={createOpen} onOpenChange={setCreateOpen} onCreate={handleCreate} />
    </div>
  );
}
